/**
 * Vista "Consumo" (RES-47): Σ ventas netas de reversas por insumo, con el top N por costo o
 * cantidad y la tabla completa (con el costo por local en el consolidado).
 */
import React, { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatCurrency } from '../../utils/formatCurrency';
import {
  consumptionTop,
  formatCompactCurrency,
  formatQty,
  money,
} from '../../utils/inventoryReports';
import { ChartTooltip, KpiTiles, MoneyNote, SortableTable, axisProps } from './ReportParts';

const ROW_HEIGHT = 32;

function ConsumptionReport({ data, t, locale, colors, nameOf }) {
  const [metric, setMetric] = useState('cost');
  const items = useMemo(() => data?.items || [], [data]);
  const totals = money(data?.totals);
  const top = useMemo(() => consumptionTop(items, metric), [items, metric]);
  const restaurantIds = useMemo(
    () => (data?.consolidated ? (data.restaurants || []).map((r) => r.restaurantId) : []),
    [data]
  );

  const valueText = (row) =>
    metric === 'cost' ? formatCurrency(row.value) : `${formatQty(row.value, locale)} ${row.unit}`;

  const restaurantTotals = useMemo(
    () =>
      restaurantIds.map((restaurantId) => {
        let cost = 0;
        let count = 0;
        items.forEach((item) => {
          const r = item.byRestaurant?.[restaurantId];
          if (!r) return;
          count += 1;
          cost += money(r.cost).value;
        });
        return { restaurantId, name: nameOf(restaurantId), cost, count };
      }),
    [items, restaurantIds, nameOf]
  );

  const columns = [
    {
      key: 'name',
      label: t('inventory.reports.columns.item'),
      sortValue: (r) => r.name,
      render: (r) => r.name,
    },
    {
      key: 'category',
      label: t('inventory.reports.columns.category'),
      sortValue: (r) => r.category,
      render: (r) => r.category || '—',
    },
    {
      key: 'sold',
      label: t('inventory.reports.columns.sold'),
      align: 'right',
      sortValue: (r) => r.sold,
      render: (r) => formatQty(r.sold, locale),
    },
    {
      key: 'reversed',
      label: t('inventory.reports.columns.reversed'),
      align: 'right',
      sortValue: (r) => r.reversed,
      render: (r) => formatQty(r.reversed, locale),
    },
    {
      key: 'quantity',
      label: t('inventory.reports.columns.netQuantity'),
      align: 'right',
      sortValue: (r) => r.quantity,
      render: (r) => `${formatQty(r.quantity, locale)} ${r.unit}`,
    },
    {
      key: 'cost',
      label: t('inventory.reports.columns.cost'),
      align: 'right',
      sortValue: (r) => money(r.cost).value,
      render: (r) => (
        <>
          {formatCurrency(money(r.cost).value)}
          <MoneyNote amount={r.cost} t={t} />
        </>
      ),
    },
    ...restaurantIds.map((restaurantId) => ({
      key: `rest-${restaurantId}`,
      label: nameOf(restaurantId),
      align: 'right',
      sortValue: (r) => money(r.byRestaurant?.[restaurantId]?.cost).value,
      render: (r) => {
        const entry = r.byRestaurant?.[restaurantId];
        return entry ? formatCurrency(money(entry.cost).value) : '—';
      },
    })),
  ];

  const share = (value) => (totals.value ? `${Math.round((value / totals.value) * 100)}%` : '—');

  return (
    <div className="report-view" data-view="consumption">
      <KpiTiles
        tiles={[
          {
            key: 'cost',
            label: t('inventory.reports.consumption.kpiCost'),
            value: formatCurrency(totals.value),
            sub: <MoneyNote amount={data?.totals} t={t} />,
          },
          {
            key: 'items',
            label: t('inventory.reports.consumption.kpiItems'),
            value: String(items.length),
          },
        ]}
      />

      <section className="report-card">
        <div className="report-card-header">
          <h3>{t('inventory.reports.consumption.chartTitle', { count: top.length })}</h3>
          <div className="report-segmented" role="group">
            {['cost', 'quantity'].map((m) => (
              <button
                key={m}
                type="button"
                className={metric === m ? 'is-active' : ''}
                aria-pressed={metric === m}
                onClick={() => setMetric(m)}
                data-metric={m}
              >
                {t(`inventory.reports.consumption.metric.${m}`)}
              </button>
            ))}
          </div>
        </div>
        {metric === 'quantity' && (
          <p className="report-hint">{t('inventory.reports.consumption.quantityHint')}</p>
        )}
        {top.length > 0 ? (
          <div
            className="report-chart"
            role="img"
            aria-label={t('inventory.reports.consumption.chartTitle', { count: top.length })}
          >
            <ResponsiveContainer width="100%" height={top.length * ROW_HEIGHT + 40}>
              <BarChart
                data={top}
                layout="vertical"
                margin={{ top: 4, right: 24, left: 8, bottom: 4 }}
              >
                <CartesianGrid horizontal={false} stroke={colors.grid} />
                <XAxis
                  type="number"
                  {...axisProps(colors)}
                  tickFormatter={(v) =>
                    metric === 'cost' ? formatCompactCurrency(v, locale) : formatQty(v, locale)
                  }
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={140}
                  {...axisProps(colors)}
                  tick={{ fill: colors.text, fontSize: 12 }}
                />
                <Tooltip
                  cursor={{ fill: colors.grid, opacity: 0.4 }}
                  content={
                    <ChartTooltip
                      rows={(payload) => [
                        {
                          key: 'value',
                          label: t(`inventory.reports.consumption.metric.${metric}`),
                          value: valueText(payload[0].payload),
                          color: colors.series1,
                        },
                      ]}
                    />
                  }
                />
                <Bar
                  dataKey="value"
                  fill={colors.series1}
                  radius={[0, 4, 4, 0]}
                  barSize={16}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="report-hint">{t('inventory.reports.states.noChartData')}</p>
        )}
      </section>

      {restaurantTotals.length > 0 && (
        <section className="report-card">
          <h3>{t('inventory.reports.compareTitle')}</h3>
          <SortableTable
            t={t}
            testId="consumption-by-restaurant"
            rows={restaurantTotals}
            rowKey={(r) => r.restaurantId}
            defaultSort={{ key: 'cost', direction: 'desc' }}
            columns={[
              {
                key: 'name',
                label: t('inventory.reports.columns.restaurant'),
                sortValue: (r) => r.name,
                render: (r) => r.name,
              },
              {
                key: 'count',
                label: t('inventory.reports.columns.itemsCount'),
                align: 'right',
                sortValue: (r) => r.count,
                render: (r) => r.count,
              },
              {
                key: 'cost',
                label: t('inventory.reports.columns.cost'),
                align: 'right',
                sortValue: (r) => r.cost,
                render: (r) => formatCurrency(r.cost),
              },
              {
                key: 'share',
                label: t('inventory.reports.columns.share'),
                align: 'right',
                sortValue: (r) => r.cost,
                render: (r) => share(r.cost),
              },
            ]}
          />
        </section>
      )}

      <section className="report-card">
        <h3>{t('inventory.reports.consumption.tableTitle')}</h3>
        <SortableTable
          t={t}
          testId="consumption-table"
          caption={t('inventory.reports.consumption.tableTitle')}
          columns={columns}
          rows={items}
          rowKey={(r) => r.itemId}
          defaultSort={{ key: 'cost', direction: 'desc' }}
        />
      </section>
    </div>
  );
}

export default ConsumptionReport;
